import { FC } from 'react';
import classNames from 'classnames';
import {
  ClipboardCheckIcon,
  MailIcon,
  EyeIcon,
} from '@heroicons/react/outline';
import {
  ActivityListDataFragment,
  AssessmentCategory,
} from '../../../generated/graphql';
import { getShortDate } from '../../lib/time';
import AssessmentCategoryBadge from './AssessmentCategoryBadge';
import ActionButton from '../ActionButton';
import ActionButtonGroup from '../ActionButtonGroup';
import TableHeader from '../TableHeader';
import { ColumnHeaderType } from '../../types/tables';
import { ProviderActivityObject } from '../../lib/providerActivity';

type StandardAssessmentsTableProps = {
  assessments: ActivityListDataFragment[];
  setSelectedProviderActivity: React.Dispatch<
    React.SetStateAction<ActivityListDataFragment | undefined>
  >;
  setIsProviderActivitySlideoverOpen?: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  sendProviderActivitiesInFollowUp?: (
    providerActivities: ActivityListDataFragment[],
  ) => void;
  showEmptyTableFilterState?: boolean;
  followUpMode?: boolean;
  selectedProviderActivitiesObject?: ProviderActivityObject;
  setSelectedProviderActivitiesObject?: React.Dispatch<
    React.SetStateAction<ProviderActivityObject>
  >;
  onCheckboxClick?: (event: React.MouseEvent<HTMLInputElement>) => void;
};

const columns: ColumnHeaderType[] = [
  {
    field: 'assessmentName',
    headerName: 'Assessment Name',
    isSortable: false,
  },
  {
    field: 'category',
    headerName: 'Category',
    isSortable: false,
  },
  {
    field: 'length',
    headerName: 'Length',
    isSortable: false,
  },
  {
    field: 'created',
    headerName: 'Added',
    isSortable: false,
  },
];

const StandardAssessmentsTable: FC<StandardAssessmentsTableProps> = ({
  assessments,
  setSelectedProviderActivity,
  setIsProviderActivitySlideoverOpen,
  sendProviderActivitiesInFollowUp,
  showEmptyTableFilterState = false,
  followUpMode = false,
  selectedProviderActivitiesObject,
  setSelectedProviderActivitiesObject,
  onCheckboxClick,
}) => {
  const handleRowClick = (assessment: ActivityListDataFragment) => {
    if (followUpMode && setSelectedProviderActivitiesObject) {
      const assessmentId = assessment.id;
      if (!selectedProviderActivitiesObject?.[assessmentId]) {
        setSelectedProviderActivitiesObject({
          ...selectedProviderActivitiesObject,
          [assessmentId]: assessment,
        });
      } else {
        const { [assessmentId]: _, ...rest } = selectedProviderActivitiesObject;
        setSelectedProviderActivitiesObject(rest);
      }
    } else {
      setSelectedProviderActivity(assessment);
      setIsProviderActivitySlideoverOpen?.(true);
    }
  };

  const handleSendToClient = (
    e: React.MouseEvent,
    assessment: ActivityListDataFragment,
  ) => {
    e.stopPropagation();
    sendProviderActivitiesInFollowUp?.([assessment]);
  };

  const handleCheckboxInteraction = (
    e: React.MouseEvent,
    assessment: ActivityListDataFragment,
  ) => {
    e.stopPropagation();
    if (setSelectedProviderActivitiesObject) {
      const assessmentId = assessment.id;
      if (!selectedProviderActivitiesObject?.[assessmentId]) {
        setSelectedProviderActivitiesObject({
          ...selectedProviderActivitiesObject,
          [assessmentId]: assessment,
        });
      } else {
        const { [assessmentId]: _, ...rest } = selectedProviderActivitiesObject;
        setSelectedProviderActivitiesObject(rest);
      }
    }
  };

  if (showEmptyTableFilterState) {
    return (
      <div className="flex h-full w-full items-center justify-center py-12">
        <div className="text-center">
          <p className="text-caption text-neutral-125">
            No assessments found matching your filters.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full">
      <div className="h-full overflow-hidden overflow-y-auto rounded-lg border border-neutral-100">
        <table className="min-w-full">
          <thead className="sticky top-0 z-20 h-[40px] w-full border-b border-neutral-100 bg-white">
            <tr className="border-b border-neutral-75 bg-white">
              {followUpMode && (
                <th scope="col" className="w-16 bg-white text-left">
                  <span className="sr-only">Select</span>
                </th>
              )}
              {columns.map((column, i) => {
                const isLastColumn = i === columns.length - 1;
                return (
                  <TableHeader
                    key={`standardAssessmentsTableColumnHeader_${column.field}`}
                    column={column}
                    isLastColumn={isLastColumn}
                    className="text-caption"
                  />
                );
              })}
              <th scope="col" className="w-[120px] bg-white text-left">
                <span className="text-caption text-neutral-125">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {assessments.map((assessment, index) => {
              const assessmentData =
                assessment.__typename === 'ActivityAssessment'
                  ? assessment.assessment
                  : null;

              if (!assessmentData) return null;

              const shortName = assessmentData.shortName || '';
              const name = assessmentData.name || '';
              const category =
                assessmentData.category as AssessmentCategory | null;
              const estimatedLength = assessmentData.estimatedLengthInMinutes;
              const createdAt = assessmentData.createdAt;

              const isLastRow = index === assessments.length - 1;
              const assessmentId = assessment.id;
              const isChecked = Boolean(
                selectedProviderActivitiesObject?.[assessmentId],
              );

              return (
                <tr
                  key={`standardAssessmentRow_${assessment.id}`}
                  className={classNames(
                    'relative h-[72px] cursor-pointer text-ellipsis text-caption text-secondary-150 hover:bg-neutral-25',
                    !isLastRow && 'border-b border-b-neutral-75',
                  )}
                  onClick={() => handleRowClick(assessment)}
                >
                  {followUpMode && (
                    <td
                      className="py-0 pl-6 pr-4 sm:w-16 sm:px-8"
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                    >
                      {isChecked && (
                        <div className="inset-y-0 left-0 w-0.5 bg-white" />
                      )}
                      <input
                        type="checkbox"
                        checked={isChecked}
                        className="h-5 w-5 cursor-pointer rounded border-neutral-100 text-secondary-125 focus:ring-2 focus:ring-secondary-125"
                        onChange={(e) => {
                          onCheckboxClick?.(
                            e.nativeEvent as unknown as React.MouseEvent<HTMLInputElement>,
                          );
                          handleCheckboxInteraction(
                            e as unknown as React.MouseEvent,
                            assessment,
                          );
                        }}
                      />
                    </td>
                  )}
                  {/* Assessment Name */}
                  <td className="max-w-xs">
                    <div className="-mt-px flex h-12 items-center">
                      <div className="flex items-center gap-4">
                        <ClipboardCheckIcon className="h-6 w-6 flex-shrink-0 text-neutral-125" />
                        <div className="flex min-w-0 flex-col">
                          <div className="truncate text-caption font-medium text-green-150">
                            {shortName}
                          </div>
                          <div className="truncate text-small-caption text-neutral-125">
                            {name}
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* Category */}
                  <td className="min-w-[140px]">
                    <div className="-mt-px flex h-12 items-center">
                      <AssessmentCategoryBadge category={category} />
                    </div>
                  </td>

                  {/* Length */}
                  <td className="min-w-[100px]">
                    <div className="-mt-px flex h-12 items-center">
                      <span className="text-caption text-neutral-150">
                        {estimatedLength ? `${estimatedLength} min` : '-'}
                      </span>
                    </div>
                  </td>

                  {/* Created */}
                  <td className="min-w-[110px]">
                    <div className="-mt-px flex h-12 items-center">
                      <span className="text-caption text-neutral-150">
                        {createdAt ? getShortDate(createdAt) : '-'}
                      </span>
                    </div>
                  </td>

                  {/* Actions */}
                  <td className="min-w-[120px]">
                    <div className="-mt-px flex h-12 items-center justify-end pr-6">
                      <ActionButtonGroup size="small">
                        {followUpMode ? (
                          <ActionButton
                            title="Preview"
                            onClick={(e) => {
                              setSelectedProviderActivity(assessment);
                              setIsProviderActivitySlideoverOpen?.(true);
                              e.stopPropagation();
                            }}
                          >
                            <EyeIcon className="h-5 w-5 text-neutral-125" />
                          </ActionButton>
                        ) : (
                          sendProviderActivitiesInFollowUp && (
                            <ActionButton
                              onClick={(e) => handleSendToClient(e, assessment)}
                              title="Send to clients"
                            >
                              <MailIcon className="h-5 w-5 text-neutral-125" />
                            </ActionButton>
                          )
                        )}
                      </ActionButtonGroup>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StandardAssessmentsTable;
